import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  TERMINAL_RUN_STATUSES,
  type DashboardChat,
  type RunStatus,
  type WorkspaceDashboard,
} from '@app/shared';
import {
  BarChart,
  Button,
  Cluster,
  Donut,
  EmptyState,
  Grid,
  Heading,
  Icon,
  Icons,
  LineChart,
  PageHeader,
  Stack,
  Surface,
  Text,
  type DonutDatum,
} from '../ui';
import { api } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useWorkspaceId } from '../lib/auth';
import { QueryState } from '../components/QueryState';
import { CostChart } from '../components/CostChart';
import { BRAND_BLUE, HeroStat, Section } from '../components/DashboardParts';
import { formatDayLabel, formatUsd } from '../lib/costChart';

/**
 * The authenticated home — a workspace-at-a-glance dashboard (§14), scoped ENTIRELY to the
 * current workspace (activity, cost, top agents, status mix, quick chat). The cross-tenant
 * PLATFORM view moved to its own operator-only page under Admin (`/app/admin/platform`,
 * #332) so this page never mixes this-workspace figures with platform totals. Charts are the
 * dependency-free inline-SVG `@/ui` primitives (LineChart / BarChart / Donut) plus the
 * existing stacked CostChart. `HeroStat` / `Section` are shared with the platform page via
 * `components/DashboardParts`.
 */

/**
 * Fixed, distinct hues for the run-status donut. Leaning semantic (green = completed,
 * red = failed/blocked) but every slice is also named + counted in the legend, so the mix
 * never relies on color alone. Unknown statuses fall back to a recessive neutral.
 */
const STATUS_COLOR: Record<RunStatus, string> = {
  COMPLETED: '#1baf7a',
  RUNNING: BRAND_BLUE,
  QUEUED: '#4a3aa7',
  WAITING: '#0f8fa8',
  WAITING_FOR_APPROVAL: '#eda100',
  BLOCKED: '#eb6834',
  FAILED: '#e34948',
  LIMIT_EXCEEDED: '#b23b39',
  LIMIT_REACHED: '#e87ba4',
  CANCELLED: '#8a9199',
};

/** `SCREAMING_SNAKE` → `Sentence case` (matches RunStatusBadge's humanisation). */
function humanizeStatus(status: string): string {
  const words = status.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const TERMINAL = new Set<string>(TERMINAL_RUN_STATUSES);

export function DashboardPage() {
  const workspaceId = useWorkspaceId();
  const dashboard = useQuery({
    queryKey: qk.dashboard(workspaceId),
    queryFn: () => api.getWorkspaceDashboard(workspaceId),
  });

  return (
    <Stack gap="6" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="This workspace at a glance — activity, cost and health."
      />

      <QueryState query={dashboard} loading={<DashboardSkeleton />}>
        {(d) => <WorkspaceSection data={d} />}
      </QueryState>
    </Stack>
  );
}

/* ── Workspace section ─────────────────────────────────────────────────────── */

function WorkspaceSection({ data: d }: { data: WorkspaceDashboard }) {
  const navigate = useNavigate();

  const costTotal = useMemo(
    () => d.cost.days.reduce((sum, day) => sum + day.totalUsd, 0),
    [d.cost.days],
  );

  const successRate = useMemo(() => {
    const terminal = d.runs.byStatus
      .filter((s) => TERMINAL.has(s.status))
      .reduce((sum, s) => sum + s.count, 0);
    if (terminal === 0) return null;
    const completed = d.runs.byStatus.find((s) => s.status === 'COMPLETED')?.count ?? 0;
    return Math.round((completed / terminal) * 100);
  }, [d.runs.byStatus]);

  const runsSeries = useMemo(
    () => d.runsOverTime.map((r) => ({ label: formatDayLabel(r.date), value: r.count })),
    [d.runsOverTime],
  );

  const statusData: DonutDatum[] = useMemo(
    () =>
      d.runs.byStatus.map((s) => ({
        id: s.status,
        label: humanizeStatus(s.status),
        value: s.count,
        color: STATUS_COLOR[s.status] ?? '#8a9199',
      })),
    [d.runs.byStatus],
  );

  const agentData = useMemo(
    () => d.runsByAgent.map((a) => ({ id: a.agentId, label: a.agentName, value: a.count })),
    [d.runsByAgent],
  );

  // A pristine workspace (no agents, no runs, no chat) gets a teaching hero instead of empty
  // charts. Chat sessions count too, so a chat-only workspace still lands on its dashboard.
  if (d.agents.total === 0 && d.runs.total === 0 && d.chat.sessions === 0) {
    return (
      <Surface bordered radius="lg" padding="6" data-testid="dashboard-empty">
        <EmptyState
          tone="brand"
          icon={Icons.Rocket}
          title="Your workspace is ready"
          description="Create your first agent to start automating email and calendar work. Runs, cost and health will light up here as it works."
          action={
            <Button onClick={() => void navigate({ to: '/app/agents' })} data-testid="dashboard-empty-cta">
              Create your first agent
            </Button>
          }
        />
      </Surface>
    );
  }

  return (
    <Stack gap="5">
      {/* Overview: a subtle recessed panel carrying the headline stats + the run trend —
          a single confident band, not a row of identical metric cards. */}
      <Surface tone="subtle" bordered radius="lg" padding="6" data-testid="dashboard-overview">
        <Stack gap="6">
          <Cluster justify="between" align="baseline" wrap>
            <Text size="sm" tone="muted">
              Last {d.windowDays} days
            </Text>
            {d.agents.archived > 0 && (
              <Text size="sm" tone="muted">
                {d.agents.archived} archived {d.agents.archived === 1 ? 'agent' : 'agents'}
              </Text>
            )}
          </Cluster>

          <Cluster gap="8" align="end" wrap>
            <HeroStat
              label="Runs"
              value={d.runs.windowTotal.toLocaleString()}
              hint={`${d.runs.total.toLocaleString()} all-time`}
              emphasis
              testid="dashboard-metric-runs"
            />
            <HeroStat
              label="Active agents"
              value={d.agents.active.toLocaleString()}
              hint={`of ${d.agents.total.toLocaleString()} total`}
              testid="dashboard-metric-agents"
            />
            <HeroStat
              label="Spend"
              value={formatUsd(costTotal)}
              hint={`this workspace · last ${d.cost.windowDays} days`}
              testid="dashboard-metric-cost"
            />
            <HeroStat
              label="Success rate"
              value={successRate === null ? '—' : `${successRate}%`}
              hint="of finished runs"
              testid="dashboard-metric-success"
            />
          </Cluster>

          <Stack gap="1">
            <Heading level={2} size="sm">
              Runs over time
            </Heading>
            {d.runs.windowTotal === 0 ? (
              <Text size="sm" tone="secondary" data-testid="dashboard-runs-over-time-empty">
                No runs in the last {d.windowDays} days. Trigger an agent — its activity will
                chart here.
              </Text>
            ) : (
              <LineChart
                data={runsSeries}
                height={200}
                color={BRAND_BLUE}
                ariaLabel={`Runs per day over the last ${d.windowDays} days`}
                data-testid="dashboard-runs-over-time"
              />
            )}
          </Stack>
        </Stack>
      </Surface>

      {/* Two focused breakdowns side by side; they stack on narrow viewports. */}
      <Grid minChildWidth="20rem" gap="5">
        <Section
          title="Top agents by runs"
          subtitle="All-time"
          testid="dashboard-runs-by-agent"
        >
          {agentData.length === 0 ? (
            <Text size="sm" tone="secondary" data-testid="dashboard-runs-by-agent-empty">
              No agent runs yet.
            </Text>
          ) : (
            <BarChart
              data={agentData}
              color={BRAND_BLUE}
              ariaLabel="Top agents by run volume"
              maxBars={6}
            />
          )}
        </Section>

        <Section title="Runs by status" subtitle="All-time" testid="dashboard-runs-by-status">
          <Donut
            data={statusData}
            ariaLabel="Run outcomes by status"
            centerValue={d.runs.total.toLocaleString()}
            centerLabel="total runs"
          />
        </Section>
      </Grid>

      {/* Quick chat — the workspace assistant, reported SEPARATELY from agent runs (§14.5).
          A pink brand icon chip + its own copy keep it distinct from the blue run band above,
          so members read it as a different feature, not another run metric. */}
      <ChatSection chat={d.chat} windowDays={d.windowDays} />

      {/* The existing stacked cost chart, verbatim — wrapped for a stable dashboard testid. */}
      <div data-testid="dashboard-cost">
        <CostChart data={d.cost} title={`Cost — last ${d.cost.windowDays} days`} />
      </div>
    </Stack>
  );
}

/** EHE brand pink — the quick-chat accent, distinguishing it from the blue run metrics. */
const BRAND_PINK = '#f09ad7';

/**
 * The Quick-chat activity card (§14.5). Reported on its own terms — sessions, messages, and
 * tool uses — NOT folded into the run metrics. A pink brand icon chip (the same pill the
 * leaderboard ranks use) + its own copy set it apart from the blue run overview band; the
 * metrics reuse `HeroStat` for a consistent rhythm.
 */
function ChatSection({ chat, windowDays }: { chat: DashboardChat; windowDays: number }) {
  return (
    <Surface bordered radius="lg" padding="6" data-testid="dashboard-chat">
      <Stack gap="5">
        <Cluster gap="3" align="center">
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2.25rem',
              height: '2.25rem',
              borderRadius: '999px',
              backgroundColor: BRAND_PINK,
              color: '#141414',
            }}
          >
            <Icon as={Icons.MessagesSquare} size="md" tone="current" />
          </span>
          <Stack gap="1" style={{ minWidth: 0 }}>
            <Heading level={2} size="md">
              Quick chat
            </Heading>
            <Text size="sm" tone="secondary">
              Your workspace assistant — reported separately from agent runs.
            </Text>
          </Stack>
        </Cluster>

        <Cluster gap="8" align="end" wrap data-testid="dashboard-chat-metrics">
          <HeroStat
            label="Sessions"
            value={chat.sessions.toLocaleString()}
            hint="conversations"
            emphasis
            testid="dashboard-chat-metric-sessions"
          />
          <HeroStat
            label="Messages"
            value={chat.messages.toLocaleString()}
            hint={`${chat.messagesWindow.toLocaleString()} in last ${windowDays} days`}
            testid="dashboard-chat-metric-messages"
          />
          <HeroStat
            label="Tool uses"
            value={chat.toolUses.toLocaleString()}
            hint="actions taken"
            testid="dashboard-chat-metric-tooluses"
          />
        </Cluster>
      </Stack>
    </Surface>
  );
}

/* ── Loading skeleton ──────────────────────────────────────────────────────── */

function DashboardSkeleton() {
  return (
    <Stack gap="5" aria-busy data-testid="dashboard-loading">
      <Surface tone="subtle" bordered radius="lg" padding="6">
        <Stack gap="6">
          <Cluster gap="8" wrap>
            {Array.from({ length: 4 }, (_, i) => (
              <Stack key={i} gap="1" style={{ minWidth: '6rem' }}>
                <SkeletonBar w="4rem" h="0.75rem" />
                <SkeletonBar w="5rem" h="2rem" />
              </Stack>
            ))}
          </Cluster>
          <SkeletonBar w="100%" h="12rem" />
        </Stack>
      </Surface>
      <Grid minChildWidth="20rem" gap="5">
        <Surface bordered radius="lg" padding="5">
          <SkeletonBar w="100%" h="10rem" />
        </Surface>
        <Surface bordered radius="lg" padding="5">
          <SkeletonBar w="100%" h="10rem" />
        </Surface>
      </Grid>
    </Stack>
  );
}

/** A minimal shimmer block (uses the surface.subtle token via a Surface). */
function SkeletonBar({ w, h }: { w: string; h: string }) {
  return (
    <Surface
      tone="subtle"
      radius="md"
      aria-hidden
      style={{ width: w, height: h, opacity: 0.6 }}
    />
  );
}
